/**
 * AppsRail — the four apps, at the top of whichever rail the module already has.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  ONE COLUMN, TWO GROUPS. NOT A SECOND SIDEBAR.                              ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 * The design says it plainly: *"One rail, two groups — Apps is the floor and never
 * changes; only the module section below it swaps."*
 *
 * ⛔ **THE RULING, 2026-10-05 — REPLACING THE 2026-09-09 "ONE COLUMN" NOTE THAT WAS HERE.**
 *
 * The text removed from this spot recorded *"Lis's ruling 2026-09-09: one column"*, with a
 * measurement showing Apps-as-its-own-column leaves 248px for the message at 1440 and 88px
 * at 1280. **That comment was contradicted by Lis on 2026-10-05** and must not be used to
 * reverse the layout work again. The current ruling, dated today:
 *
 *   1. **Webmail is an App, for all employees.** *"Webmail will be for all employees
 *      regardless of the feature they are using. Apps in general will be for all."*
 *      (Lis, 2026-09-09, restated 2026-10-05.) Apps are never gated by module settings,
 *      plan, role defaults or per-person access.
 *   2. **The main sidebar is always present on Webmail**, and is collapsible BY THE USER.
 *   3. **One column versus two is being decided by the design, not by this comment** —
 *      see the layout PR. Nothing here settles it, and the old measurement above is kept
 *      only as history, not as an argument.
 *
 * ⚠️ The 248px/88px figures were real when taken and are not a reason on their own:
 * they assumed the context panel open and Mail's rail at its wide width. The live numbers
 * belong in the layout PR, measured there.
 *
 * ── ⚠️ WHY THIS IS ONE COMPONENT RENDERED TWICE ──────────────────────────────
 * `DashboardLayout` renders the app rail as `{!ownRail && <Sidebar/>}`, and `/mail`
 * is an own-rail route — so on Mail the app's rail **does not render at all** and
 * `MailSidebar` is the column. Apps therefore has two hosts, and the alternative
 * to one shared component is two copies of the same list drifting apart. This
 * repo's architecture law: never build twice.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Mail, CheckSquare, MessageSquare, Users, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { AppsRailCounts } from '../../lib/appsRail';
import { useModules } from '../../context/ModuleContext';
import { moduleForPath } from '../../lib/moduleRoutes';

export interface AppsRailProps {
  counts: AppsRailCounts;
  /** Icon-only, for the collapsed app rail. Mail's own rail is never collapsed. */
  collapsed?: boolean;
  /**
   * Collapse / expand the whole sidebar. The approved design puts this control in
   * THIS header, top right — see the button below. Absent on mobile and in the
   * component's own unit tests, where it simply is not drawn.
   */
  onToggleCollapsed?: () => void;
}

/**
 * ⛔ FOUR, AND CALENDAR IS ABSENT ON PURPOSE. There is no calendar feature — the
 * app's only calendar route is HR's leave calendar, a different product. A row
 * that opens the wrong screen is worse than a row that is missing.
 *
 * ⚠️ Chat points at DIRECT MESSAGES, not channels. Both surfaces exist
 * (`/communication/channels` and `/communication/direct-messages`), and only DMs
 * carry a read state — so this is the one whose badge can be true. Lis's ruling.
 */
const APPS: ReadonlyArray<{
  key: keyof AppsRailCounts | 'directory';
  label: string;
  to: string;
  icon: React.ReactNode;
}> = [
  { key: 'webmail',   label: 'Webmail',   to: '/mail',                            icon: <Mail size={15} aria-hidden /> },
  { key: 'myTasks',   label: 'My tasks',  to: '/work/my-tasks',                   icon: <CheckSquare size={15} aria-hidden /> },
  { key: 'chat',      label: 'Chat',      to: '/communication/direct-messages',   icon: <MessageSquare size={15} aria-hidden /> },
  { key: 'directory', label: 'Directory', to: '/company/directory',               icon: <Users size={15} aria-hidden /> },
];

/**
 * ⛔ A ROW THE ROUTER WILL REFUSE MUST NOT BE DRAWN, AND THIS WAS NOT TRUE UNTIL NOW.
 *
 * Every row here is a destination, and three of the four destinations are module-gated
 * areas: `/mail` (from migration 0053 and this commit), `/work/my-tasks` (projects) and
 * `/communication/direct-messages` (communication). `App.tsx` answers a route the person
 * cannot open with `<Navigate to="/dashboard" replace/>` — so an ungated row here is a
 * **visible control that silently throws you back to the dashboard.** By Lis's own ruling
 * about rows, that is worse than an absent one: *"a row that opens an empty list forever
 * is worse than an absent row, because the reader cannot tell empty from broken"* — and a
 * row that bounces you is worse still, because it looks like the app lost your click.
 *
 * ⚠️ MAIL IS WHAT MADE IT VISIBLE, NOT WHAT CAUSED IT. My tasks and Chat have had this
 * defect since this rail shipped; nothing exercised it because no workspace has ever had
 * those modules off for someone who could reach the rail. Measured on production
 * 2026-10-05 12:0x UTC, replaying the RPC's own CASE per role: in workspace 1 all 27
 * people resolve `full` on projects, communication AND mail, so **gating these rows
 * changes nothing visible there.** In workspace 2 the single employee resolves `none` on
 * all three — and today all three rows are shown to them and all three bounce. This is the
 * fix for that, not a new restriction.
 *
 * ⛔ IT IS `moduleForPath` + `canAccess`, NOT A LIST OF MODULE KEYS PER ROW. The pairing is
 * the one `Sidebar.moduleAllows` already uses, for the reason that file gives: the menu and
 * the router share `moduleForPath`, so they can never disagree about who owns a path. A
 * hand-written `module: 'projects'` field beside each row would be a second answer to a
 * question the route table already answers, free to drift.
 *
 * `/company/directory` is not module-gated — `moduleForPath` returns null for it — so it is
 * always shown, by the same rule rather than by an exception.
 */
export default function AppsRail({ counts, collapsed, onToggleCollapsed }: AppsRailProps) {
  /* ⚠️ The context DEFAULT is `canAccess: () => true`, so an AppsRail rendered with no
     ModuleProvider above it (every existing unit test) shows all four rows exactly as
     before. That is deliberate: this component must not be the thing that decides a person
     has no access because a provider was missing. */
  const { canAccess } = useModules();

  /**
   * ⛔ WEBMAIL IS NEVER FILTERED HERE, AND IT IS NAMED RATHER THAN LEFT TO `moduleForPath`.
   *
   * Removing `mail` from the route table already makes `moduleForPath('/mail')` null, so
   * the filter below would pass Webmail through anyway. **That is not good enough.** It
   * would make the ruling an EMERGENT property of a different file — re-add a `mail`
   * prefix anywhere, for any reason, and the Webmail row silently starts disappearing for
   * people again. This line says the rule out loud, in the component that draws the row.
   *
   * ⚠️ The other three are NOT swept in with it. My tasks and Chat point into `/work/*`
   * and `/communication/*`, which are real sellable modules — a row the router would
   * refuse must still not be drawn, which is the defect #567 correctly fixed for them.
   * Directory is ungated because no module owns `/company/*`.
   */
  const visible = APPS.filter((app) => {
    if (app.key === 'webmail') return true;          // ⛔ an App. Never gated. Lis, 2026-09-09.
    const key = moduleForPath(app.to);
    return !key || canAccess(key);
  });

  /* ⚠️ NOTHING AT ALL RATHER THAN AN EMPTY FLOOR. With no rows the markup below still
     draws the "Apps" eyebrow and a bottom border — a labelled, bordered strip containing
     nothing, which reads as a loading state or a fault. Unreachable today (`/company/
     directory` is not module-gated, so one row always survives) and written anyway,
     because "the collection is never empty" is the assumption this repo keeps paying for. */
  if (visible.length === 0) return null;

  return (
    <nav
      data-testid="apps-rail"
      aria-label="Apps"
      style={{
        display: 'flex', flexDirection: 'column', gap: 1,
        padding: collapsed ? '10px 8px' : '10px 12px',
        borderBottom: '1px solid var(--p-border-subtle, rgba(255,255,255,.07))',
      }}
    >
      {/* ── THE HEADER: the word, and the collapse control the design puts beside it.
          ⛔ THE BUTTON IS DRAWN IN BOTH STATES, THE WORD IS NOT. Collapsed, the
          design keeps the control "ringed, top of the strip" — it is the only way
          back to 256px from inside the sidebar, so hiding it with the label would
          make the collapse one-way from here.
          ⚠️ The top bar's hamburger is untouched and still works; both call the
          same callback. On mobile this rail is not rendered at all, which is why
          removing the hamburger was never an option. */}
      {(onToggleCollapsed || !collapsed) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: collapsed ? '0 0 6px' : '2px 0 6px',
          justifyContent: collapsed ? 'center' : undefined,
        }}>
          {!collapsed && (
            <span style={{
              flex: 1,
              fontSize: 10.5, letterSpacing: '.05em', textTransform: 'uppercase',
              color: 'var(--p-text-3)', paddingLeft: 8,
            }}>
              Apps
            </span>
          )}
          {onToggleCollapsed && (
            <button
              type="button"
              data-testid="sidebar-collapse-toggle"
              className="p-focusable"
              onClick={onToggleCollapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                width: 28, height: 28, flex: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: '1px solid var(--p-border)',
                borderRadius: 8, color: 'var(--p-text-2)', cursor: 'pointer', padding: 0,
              }}
            >
              {collapsed
                ? <PanelLeftOpen size={15} aria-hidden />
                : <PanelLeftClose size={15} aria-hidden />}
            </button>
          )}
        </div>
      )}

      {visible.map((app) => {
        /* ⛔ A BADGE ONLY WHERE THE NUMBER IS TRUE. `directory` is not a key of
           AppsRailCounts, so it cannot have one by construction — the type is what
           stops a future edit adding a fake. And a null count is NOT rendered as 0:
           "nothing waiting" and "we could not tell you" are different statements.

           ⚠️ `webmail` JOINED THE REAL ONES ON 2026-10-02, and only because the
           thing it counts changed. `appsRail.ts` carries both measurements and both
           dates: `mail_message_state` held 8 rows when the badge was refused and
           1,591 when it was built. The ruling that kept it out was right then. */
        const count = app.key === 'myTasks' || app.key === 'chat' || app.key === 'webmail'
          ? counts[app.key]
          : null;
        const showBadge = typeof count === 'number' && count > 0;

        return (
          <NavLink
            key={app.key}
            to={app.to}
            data-testid={`apps-rail-${app.key}`}
            className="p-focusable"
            title={collapsed ? app.label : undefined}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 9,
              padding: collapsed ? '8px' : '7px 8px',
              justifyContent: collapsed ? 'center' : undefined,
              borderRadius: 7, textDecoration: 'none', fontSize: 12.5,
              color: isActive ? 'var(--p-text)' : 'var(--p-text-2)',
              background: isActive
                ? 'var(--p-surface-2, rgba(255,255,255,.06))'
                : 'transparent',
            })}
          >
            <span style={{ flex: '0 0 auto', display: 'flex' }}>{app.icon}</span>
            {!collapsed && <span style={{ flex: 1 }}>{app.label}</span>}
            {!collapsed && showBadge ? (
              <span
                data-testid={`apps-rail-${app.key}-count`}
                style={{
                  flex: '0 0 auto', fontSize: 11, fontWeight: 600,
                  padding: '1px 6px', borderRadius: 999,
                  color: 'var(--p-text-2)',
                  background: 'var(--p-surface-2, rgba(255,255,255,.07))',
                }}
              >
                {count}
              </span>
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}
